import { createReducer, on } from '@ngrx/store';

import {
  AtcOverviewDataStoreModel,
  CapacityGraphDataStoreModel,
  CapacityOverviewDataStoreModel,
  GetAtcResponseStoreModel,
  GetNtcOptionsResponseStoreModel,
  GetNtcResponseStoreModel,
  NtcOverviewDataStoreModel,
  PlannedOutageDetailsStoreModel,
  PlannedOutageOptionsStoreModel,
  PlannedOutageStoreModel,
  UnplannedOutageDetailsStoreModel,
  UnplannedOutageOptionsStoreModel,
} from '@domain/capacity';

import { CapacityActions } from './capacity.actions';
import {
  CapacityGetAtcOverviewStoreModel,
  CapacityGetNtcGraphStoreModel,
  CapacityGetNtcOverviewStoreModel,
  CapacityNtcPlannedOutagesListStoreModel,
  CapacityNtcUnplannedOutagesListStoreModel,
} from './models';

export const featureKey = 'capacity';

export interface AppState {
  [featureKey]: FeatureState;
}

export interface FeatureState {
  isLoading: Record<string, boolean>;
  overview?: NtcOverviewDataStoreModel;
  ntc?: GetNtcResponseStoreModel;
  ntcOptions?: GetNtcOptionsResponseStoreModel;
  atc?: GetAtcResponseStoreModel;
  atcOverview?: AtcOverviewDataStoreModel;
  lastNtcOverviewRequestData?: CapacityGetNtcOverviewStoreModel;
  lastAtcOverviewRequestData?: CapacityGetAtcOverviewStoreModel;
  capacityOverview?: CapacityOverviewDataStoreModel;
  capacityGraph?: CapacityGraphDataStoreModel;
  lastCapacityGraphRequestData?: CapacityGetNtcGraphStoreModel;
  plannedOutages?: CapacityNtcPlannedOutagesListStoreModel;
  unplannedOutages?: CapacityNtcUnplannedOutagesListStoreModel;
  plannedOutageOptions?: PlannedOutageOptionsStoreModel;
  plannedOutage?: PlannedOutageDetailsStoreModel;
  unplannedOutageOptions?: UnplannedOutageOptionsStoreModel;
  unplannedOutage?: UnplannedOutageDetailsStoreModel;
  submitNtcError?: boolean;
  submitAtcError?: boolean;
  submitAtcSuccess?: boolean;
}

export const initialState: FeatureState = {
  isLoading: {},
  overview: undefined,
  ntc: undefined,
  ntcOptions: undefined,
  atc: undefined,
  atcOverview: undefined,
  lastNtcOverviewRequestData: undefined,
  lastAtcOverviewRequestData: undefined,
  capacityOverview: undefined,
  capacityGraph: undefined,
  lastCapacityGraphRequestData: undefined,
  plannedOutages: undefined,
  unplannedOutages: undefined,
  plannedOutageOptions: undefined,
  plannedOutage: undefined,
  unplannedOutageOptions: undefined,
  unplannedOutage: undefined,
  submitNtcError: undefined,
  submitAtcError: undefined,
  submitAtcSuccess: undefined,
};

export const capacityReducer = createReducer(
  initialState,

  on(
    CapacityActions.getCapacityOverview,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getCapacityOverview.type]: true } })
  ),
  on(
    CapacityActions.getCapacityOverviewSuccess,
    (state, action): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getCapacityOverview.type]: false },
      capacityOverview: action.overview,
    })
  ),
  on(
    CapacityActions.getCapacityOverviewFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getCapacityOverview.type]: false } })
  ),

  on(
    CapacityActions.getNtcOverview,
    (state, { request }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcOverview.type]: true },
      lastNtcOverviewRequestData: request,
    })
  ),
  on(
    CapacityActions.getNtcOverviewSuccess,
    (state, action): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcOverview.type]: false },
      overview: action.overview,
    })
  ),
  on(
    CapacityActions.getNtcOverviewFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcOverview.type]: false } })
  ),

  on(
    CapacityActions.getCapacityGraph,
    (state, { request }): FeatureState => ({
      ...state,
      lastCapacityGraphRequestData: request,
      isLoading: { ...state.isLoading, [CapacityActions.getCapacityGraph.type]: true },
    })
  ),
  on(
    CapacityActions.getCapacityGraphSuccess,
    (state, action): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getCapacityGraph.type]: false },
      capacityGraph: action.capacityGraph,
    })
  ),
  on(
    CapacityActions.getCapacityGraphFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getCapacityGraph.type]: false } })
  ),

  on(
    CapacityActions.getNtcPlannedOutages,
    (state, { action }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutages.type]: true },
      plannedOutages: { ...state.plannedOutages, action },
    })
  ),
  on(
    CapacityActions.getNtcPlannedOutagesSuccess,
    (state, { plannedOutages, total }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutages.type]: false },
      plannedOutages: {
        ...state.plannedOutages,
        entries: plannedOutages,
        totalEntries: total,
        offset: 0,
      },
    })
  ),
  on(
    CapacityActions.getNtcPlannedOutagesFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutages.type]: false } })
  ),

  on(
    CapacityActions.getNextNtcPlannedOutages,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNextNtcPlannedOutages.type]: true } })
  ),
  on(
    CapacityActions.getNextNtcPlannedOutagesSuccess,
    (state, { plannedOutages, total, offset }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNextNtcPlannedOutages.type]: false },
      plannedOutages: {
        ...state.plannedOutages,
        entries: [
          ...(state.plannedOutages?.entries || []),
          ...plannedOutages.filter(({ id }) => !(state.plannedOutages?.entries || []).some((plannedOutage) => plannedOutage.id === id)),
        ],
        totalEntries: total,
        offset,
      },
    })
  ),
  on(
    CapacityActions.getNextNtcPlannedOutagesFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNextNtcPlannedOutages.type]: false } })
  ),

  on(
    CapacityActions.getNtcUnplannedOutages,
    (state, { action }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutages.type]: true },
      unplannedOutages: { ...state.unplannedOutages, action },
    })
  ),
  on(
    CapacityActions.getNtcUnplannedOutagesSuccess,
    (state, { unplannedOutages, total }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutages.type]: false },
      unplannedOutages: {
        ...state.unplannedOutages,
        entries: unplannedOutages,
        totalEntries: total,
        offset: 0,
      },
    })
  ),
  on(
    CapacityActions.getNtcUnplannedOutagesFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutages.type]: false } })
  ),

  on(
    CapacityActions.getNextNtcUnplannedOutages,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNextNtcUnplannedOutages.type]: true } })
  ),
  on(
    CapacityActions.getNextNtcUnplannedOutagesSuccess,
    (state, { unplannedOutages, total, offset }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNextNtcUnplannedOutages.type]: false },
      unplannedOutages: {
        ...state.unplannedOutages,
        entries: [
          ...(state.unplannedOutages?.entries || []),
          ...unplannedOutages.filter(
            ({ id }) => !(state.unplannedOutages?.entries || []).some((unplannedOutage) => unplannedOutage.id === id)
          ),
        ],
        totalEntries: total,
        offset,
      },
    })
  ),
  on(
    CapacityActions.getNextNtcUnplannedOutagesFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNextNtcUnplannedOutages.type]: false } })
  ),

  on(
    CapacityActions.getNtcPlannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutage.type]: true } })
  ),
  on(
    CapacityActions.getNtcPlannedOutageSuccess,
    (state, { plannedOutage }): FeatureState => ({
      ...state,
      plannedOutage,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutage.type]: false },
    })
  ),
  on(
    CapacityActions.getNtcPlannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutage.type]: false } })
  ),

  on(
    CapacityActions.getNtcUnplannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutage.type]: true } })
  ),
  on(
    CapacityActions.getNtcUnplannedOutageSuccess,
    (state, { unplannedOutage }): FeatureState => ({
      ...state,
      unplannedOutage,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutage.type]: false },
    })
  ),
  on(
    CapacityActions.getNtcUnplannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutage.type]: false } })
  ),

  on(
    CapacityActions.getNtcPlannedOutageOptions,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutageOptions.type]: true } })
  ),
  on(
    CapacityActions.getNtcPlannedOutageOptionsSuccess,
    (state, { plannedOutageOptions }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutageOptions.type]: false },
      plannedOutageOptions,
    })
  ),
  on(
    CapacityActions.getNtcPlannedOutageOptionsFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcPlannedOutageOptions.type]: false } })
  ),

  on(
    CapacityActions.getNtcUnplannedOutageOptions,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutageOptions.type]: true } })
  ),
  on(
    CapacityActions.getNtcUnplannedOutageOptionsSuccess,
    (state, { unplannedOutageOptions }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutageOptions.type]: false },
      unplannedOutageOptions,
    })
  ),
  on(
    CapacityActions.getNtcUnplannedOutageOptionsFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcUnplannedOutageOptions.type]: false },
    })
  ),

  on(
    CapacityActions.createNtcPlannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.createNtcPlannedOutage.type]: true } })
  ),
  on(
    CapacityActions.createNtcPlannedOutageSuccess,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.createNtcPlannedOutage.type]: false } })
  ),
  on(
    CapacityActions.createNtcPlannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.createNtcPlannedOutage.type]: false } })
  ),

  on(
    CapacityActions.createNtcUnplannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.createNtcUnplannedOutage.type]: true } })
  ),
  on(
    CapacityActions.createNtcUnplannedOutageSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.createNtcUnplannedOutage.type]: false },
    })
  ),
  on(
    CapacityActions.createNtcUnplannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.createNtcUnplannedOutage.type]: false } })
  ),

  on(
    CapacityActions.updateNtcPlannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.updateNtcPlannedOutage.type]: true } })
  ),
  on(
    CapacityActions.updateNtcPlannedOutageSuccess,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.updateNtcPlannedOutage.type]: false } })
  ),
  on(
    CapacityActions.updateNtcPlannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.updateNtcPlannedOutage.type]: false } })
  ),

  on(
    CapacityActions.updateNtcUnplannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.updateNtcUnplannedOutage.type]: true } })
  ),
  on(
    CapacityActions.updateNtcUnplannedOutageSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.updateNtcUnplannedOutage.type]: false },
    })
  ),
  on(
    CapacityActions.updateNtcUnplannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.updateNtcUnplannedOutage.type]: false } })
  ),

  on(
    CapacityActions.deleteNtcPlannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.deleteNtcPlannedOutage.type]: true } })
  ),
  on(
    CapacityActions.deleteNtcPlannedOutageSuccess,
    (state, { id }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.deleteNtcPlannedOutage.type]: false },
      plannedOutages: markPlannedOutageAsDeleted(id, state.plannedOutages),
    })
  ),
  on(
    CapacityActions.deleteNtcPlannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.deleteNtcPlannedOutage.type]: false } })
  ),

  on(
    CapacityActions.cancelNtcPlannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcPlannedOutage.type]: true } })
  ),
  on(
    CapacityActions.cancelNtcPlannedOutageSuccess,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcPlannedOutage.type]: false } })
  ),
  on(
    CapacityActions.cancelNtcPlannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcPlannedOutage.type]: false } })
  ),

  on(
    CapacityActions.cancelNtcUnplannedOutage,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcUnplannedOutage.type]: true } })
  ),
  on(
    CapacityActions.cancelNtcUnplannedOutageSuccess,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcUnplannedOutage.type]: false } })
  ),
  on(
    CapacityActions.cancelNtcUnplannedOutageFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.cancelNtcUnplannedOutage.type]: false } })
  ),

  on(
    CapacityActions.getNtcOptions,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcOptions.type]: true } })
  ),
  on(
    CapacityActions.getNtcOptionsSuccess,
    (state, action): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtcOptions.type]: false },
      ntcOptions: action.getNtcOptionsResponse,
    })
  ),
  on(
    CapacityActions.getNtcOptionsFail,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtcOptions.type]: false } })
  ),

  on(
    CapacityActions.getNtc,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getNtc.type]: true } })
  ),
  on(
    CapacityActions.getNtcSuccess,
    (state, { getNtcResponse }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtc.type]: false },
      ntc: getNtcResponse,
    })
  ),
  on(
    CapacityActions.getNtcFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getNtc.type]: false },
      ntc: undefined,
    })
  ),
  on(
    CapacityActions.submitNtc,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.submitNtc.type]: true },
      submitNtcError: undefined,
    })
  ),
  on(
    CapacityActions.submitNtcSuccess,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.submitNtc.type]: false } })
  ),
  on(
    CapacityActions.submitNtcFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.submitNtc.type]: false },
      submitNtcError: true,
    })
  ),

  on(
    CapacityActions.getAtc,
    (state): FeatureState => ({ ...state, isLoading: { ...state.isLoading, [CapacityActions.getAtc.type]: true } })
  ),
  on(
    CapacityActions.getAtcSuccess,
    (state, { getAtcResponse }): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getAtc.type]: false },
      atc: getAtcResponse,
    })
  ),
  on(
    CapacityActions.getAtcFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getAtc.type]: false },
      atc: undefined,
    })
  ),
  on(
    CapacityActions.submitAtc,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.submitAtc.type]: true },
    })
  ),
  on(
    CapacityActions.submitAtcSuccess,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.submitAtc.type]: false },
      submitAtcSuccess: true,
      submitAtcError: false,
    })
  ),
  on(
    CapacityActions.submitAtcFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.submitAtc.type]: false },
      submitAtcSuccess: false,
      submitAtcError: true,
    })
  ),

  on(CapacityActions.getAtcOverview, (state, { request, refresh }): FeatureState => {
    const newState: FeatureState = {
      ...state,
      isLoading: { ...state.isLoading },
      lastAtcOverviewRequestData: request,
    };

    if (refresh) {
      newState.isLoading[CapacityActions.refreshAtcOverview.type] = true;
    } else {
      newState.isLoading[CapacityActions.getAtcOverview.type] = true;
    }

    return newState;
  }),
  on(
    CapacityActions.getAtcOverviewSuccess,
    (state, action): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getAtcOverview.type]: false, [CapacityActions.refreshAtcOverview.type]: false },
      atcOverview: action.overview,
    })
  ),
  on(
    CapacityActions.getAtcOverviewFail,
    (state): FeatureState => ({
      ...state,
      isLoading: { ...state.isLoading, [CapacityActions.getAtcOverview.type]: false, [CapacityActions.refreshAtcOverview.type]: false },
    })
  ),

  on(CapacityActions.clearNtcPlannedOutageOptions, (state): FeatureState => ({ ...state, plannedOutageOptions: undefined })),
  on(
    CapacityActions.clearNtcPlannedOutage,
    (state): FeatureState => ({ ...state, plannedOutage: undefined, plannedOutageOptions: undefined })
  ),
  on(CapacityActions.clearNtcPlannedOutages, (state): FeatureState => ({ ...state, plannedOutages: undefined })),
  on(CapacityActions.clearNtcUnplannedOutages, (state): FeatureState => ({ ...state, unplannedOutages: undefined })),
  on(CapacityActions.clearNtcUnplannedOutageOptions, (state): FeatureState => ({ ...state, unplannedOutageOptions: undefined })),
  on(
    CapacityActions.clearNtcUnplannedOutage,
    (state): FeatureState => ({ ...state, unplannedOutage: undefined, unplannedOutageOptions: undefined })
  ),
  on(
    CapacityActions.clearNtcOverview,
    (state): FeatureState => ({
      ...state,
      overview: undefined,
      capacityGraph: undefined,
      lastNtcOverviewRequestData: undefined,
      lastCapacityGraphRequestData: undefined,
    })
  ),
  on(CapacityActions.clearCapacityOverview, (state): FeatureState => ({ ...state, capacityOverview: undefined })),
  on(
    CapacityActions.clearNtc,
    (state): FeatureState => ({
      ...state,
      ntc: undefined,
      ntcOptions: undefined,
      submitNtcError: undefined,
      isLoading: { ...state.isLoading, [CapacityActions.submitNtc.type]: false, [CapacityActions.getNtc.type]: false },
    })
  ),
  on(
    CapacityActions.clearNtcMtus,
    (state): FeatureState => ({
      ...state,
      ntc: undefined,
    })
  ),
  on(
    CapacityActions.clearAtc,
    (state): FeatureState => ({
      ...state,
      atc: undefined,
      submitAtcSuccess: undefined,
      submitAtcError: undefined,
      isLoading: { ...state.isLoading, [CapacityActions.submitAtc.type]: false, [CapacityActions.getAtc.type]: false },
    })
  ),
  on(
    CapacityActions.clearAtcOverview,
    (state): FeatureState => ({
      ...state,
      atcOverview: undefined,
      isLoading: { ...state.isLoading, [CapacityActions.getAtcOverview.type]: false, [CapacityActions.refreshAtcOverview.type]: false },
    })
  )
);

const markPlannedOutageAsDeleted = (
  id: string,
  plannedOutages: CapacityNtcPlannedOutagesListStoreModel | undefined
): CapacityNtcPlannedOutagesListStoreModel | undefined => {
  if (!plannedOutages) return;
  return {
    ...plannedOutages,
    entries: plannedOutages.entries?.map((plannedOutage): PlannedOutageStoreModel => {
      if (id !== plannedOutage.id) {
        return plannedOutage;
      }

      return {
        ...plannedOutage,
        cancellable: false,
        deletable: false,
      };
    }),
  };
};
